<template>
    <section id="portfolio" class="portfolio-component page-section">
        <div class="container">
            <header class="main-title title">Portfolio</header>
            <div class="portfolio-projects-container" :class="{'reveal-ready': observing}">
                <div v-for="(oneProject, index) in portfolioProjects" :key="index"
                     @click="openCloseProjectLocally(oneProject)"
                     @keyup.enter="openCloseProjectLocally(oneProject)"
                     tabindex="0"
                     class="portfolio-project"
                     :style="{transitionDelay: (index % 3) * 90 + 'ms'}">
                    <div class="image-container">
                        <img class="project-image" :alt="oneProject.name" :src="getImgPath(oneProject)">
                        <div class="portfolio-hover">
                            <span class="hover-name">{{oneProject.name}}</span>
                            <span class="hover-action">View details</span>
                        </div>
                    </div>
                    <div class="project-info">
                        <div class="project-heading">
                            <div class="project-name">{{oneProject.name}}</div>
                            <div class="project-date">{{oneProject.date}}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
    import {mapActions} from "vuex";

    export default {
        name: "PortfolioComponent",
        data() {
            return {
                observing: false,
                // images: optional list of screenshots for the details gallery (falls back to `image`).
                portfolioProjects: [
                    {
                        name: 'Pres Global',
                        site: 'https://www.pres.global/',
                        image: 'pg.jpg',
                        role: 'Senior Full Stack Developer',
                        description: `Cloud ticketing and operations platform for cinemas, theatres and venues, used by most cinemas in Israel and by cinemas in six European countries. As part of the development team, I developed and implemented the majority of its modules: scheduling, ticketing, box office and kiosks, reporting, and payments.`,
                        date: '2024-2026'
                    },
                    {
                        name: 'Air Doctor',
                        site: 'https://air-dr.com/',
                        image: 'air-dr.jpg',
                        role: 'Web Developer, client side',
                        description: `Platform for matching users and trusted doctors of any specialty around the world. Developed the client side at Hippotec.`,
                        date: '2017'
                    },
                    {
                        name: 'Probuild',
                        site: 'https://www.probuild.co.il/',
                        image: 'probuild.jpg',
                        role: 'Web Developer, then Full Stack Team Leader',
                        description: `Project management and ERP SaaS for large scale construction projects. Built client and server side at Hippotec as a client project, then in-house at Probuild, where I led a team of 4 developers.`,
                        date: '2016-2024'
                    },
                    {
                        name: 'iDent',
                        site: 'https://identlab.co.il/',
                        image: 'ident.jpg',
                        role: 'Design and development',
                        description: `Created a responsive business card website for a dental laboratory from concept to launch. The project focused on a clear structure, professional design, and easy access to key information such as services, technologies, and contact details. The result is a modern website that strengthens the laboratory’s brand and credibility.`,
                        date: '2026'
                    },
                    {
                        name: 'Musah',
                        site: 'https://musah.co.il/site/',
                        image: 'musah.jpg',
                        role: 'Web Developer',
                        description: `Together with PHP developer developed platform to search for auto services in Israel.`,
                        date: '2015'
                    },
                    {
                        name: 'Petek Design',
                        site: 'http://petek-design.com/',
                        image: 'petek-design.jpg',
                        role: 'Web Developer',
                        description: `Few changes to design: used Photoshop, worked with CSS.`,
                        date: '2015'
                    },
                    {
                        name: 'GorAlex',
                        site: 'https://goralex.com/',
                        image: 'goralex-php.jpg',
                        role: 'Web Developer',
                        description: `Previous version of the site goralex. It was written with framework php phalcon.`,
                        date: '2015'
                    },
                    {
                        name: 'Victoria-I.H.C.',
                        site: 'http://v-ihc.com/',
                        image: 'vihc.jpg',
                        role: 'Web Developer',
                        description: `My first work. It's simple website.`,
                        date: '2013'
                    }
                ]
            };
        },
        mounted() {
            this.setupReveal();
        },
        beforeDestroy() {
            if (this.observer) {
                this.observer.disconnect();
            }
        },
        methods: {
            setupReveal() {
                const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                if (reduceMotion || !('IntersectionObserver' in window)) {
                    return;
                }
                this.observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            entry.target.classList.add('visible');
                            this.observer.unobserve(entry.target);
                        }
                    });
                }, {threshold: 0.15});
                this.$el.querySelectorAll('.portfolio-project').forEach((el) => this.observer.observe(el));
                this.observing = true;
            },
            getImgPath(project) {
                return require(`@/assets/portfolio/` + project.image);
            },
            ...mapActions('portfolioProjectDetails', [
                'openCloseProject'
            ]),
            ...mapActions('main', [
                'isShowBackdropAction'
            ]),
            openCloseProjectLocally(oneProject) {
                this.openCloseProject(oneProject);
                this.isShowBackdropAction();
            }
        }
    }
</script>

<style lang="scss" scoped>
    #portfolio.portfolio-component {
        display: flex;
        justify-content: center;

        .container {
            max-width: 1200px;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;

            .main-title {
                margin-bottom: var(--section-title-gap, 50px);
            }

            .portfolio-projects-container {
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                gap: 32px 28px;
                width: 100%;

                .portfolio-project {
                    width: calc((100% - 56px) / 3);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    cursor: pointer;
                    background-color: #fff;
                    border: 2px solid #e9ecef;
                    border-radius: 12px;
                    transition: border-color .3s ease, box-shadow .3s ease, transform .3s ease;

                    .image-container {
                        position: relative;
                        height: 12rem;
                        overflow: hidden;
                        background-color: #e9ecef;

                        .project-image {
                            display: block;
                            width: 100%;
                            height: 100%;
                            object-fit: cover;
                            object-position: top;
                            transition: transform .6s ease;
                        }

                        .portfolio-hover {
                            position: absolute;
                            top: 0;
                            right: 0;
                            bottom: 0;
                            left: 0;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            gap: 6px;
                            opacity: 0;
                            background: rgba(44, 62, 80, .78);
                            color: #fff;
                            transition: opacity .4s ease;

                            .hover-name {
                                font-family: 'Lobster', Lobster, Arial, sans-serif;
                                font-size: 2.2em;
                                line-height: 1.1;
                                text-align: center;
                                padding: 0 12px;
                                transform: translateY(10px);
                                transition: transform .4s ease;
                            }

                            .hover-action {
                                font-size: 0.95em;
                                transform: translateY(10px);
                                transition: transform .4s ease .05s;
                            }
                        }
                    }

                    .project-info {
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                        padding: 14px 16px;

                        .project-heading {
                            display: flex;
                            align-items: baseline;
                            justify-content: space-between;
                            gap: 12px;
                        }

                        .project-name {
                            font-weight: bold;
                            font-size: 1.1em;
                            color: #2c3e50;
                        }

                        .project-date {
                            flex: none;
                            font-size: 0.9em;
                            color: #979a9c;
                        }

                    }

                    &:hover, &:focus-visible {
                        border-color: #2c3e50;
                        box-shadow: 0 10px 24px -8px rgba(44, 62, 80, .45);
                        outline: none;

                        .image-container {
                            .project-image {
                                transform: scale(1.07);
                            }

                            .portfolio-hover {
                                opacity: 1;

                                .hover-name, .hover-action {
                                    transform: none;
                                }
                            }
                        }
                    }
                }

                // Scroll reveal: cards rise in, staggered by column, when they enter the viewport.
                &.reveal-ready {
                    .portfolio-project {
                        opacity: 0;
                        transform: translateY(24px);
                        transition: opacity .6s ease-out, transform .6s ease-out,
                            border-color .3s ease, box-shadow .3s ease;

                        &.visible {
                            opacity: 1;
                            transform: none;
                        }
                    }
                }
            }
        }
    }

    /*media*/
    @media screen and (max-width: 1000px) {
        #portfolio.portfolio-component {
            .container {
                .portfolio-projects-container {
                    .portfolio-project {
                        width: calc((100% - 28px) / 2);
                    }
                }
            }
        }
    }

    @media screen and (max-width: 770px) {
        #portfolio.portfolio-component {
            .container {
                .portfolio-projects-container {
                    .portfolio-project {
                        width: 100%;
                    }
                }
            }
        }
    }
</style>
